import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createOrUpdatePortfolioGroup } from '../../api/userApi';

export const usePortfolioGroups = () => {
  const dispatch = useDispatch();
  const portfolioGroups = useSelector(state => state.user.data?.portfolio_groups || []);

  // Create a new group
  const createNewGroup = useCallback(async ({ name, portfolio_ids }) => {
    try {
      const result = await dispatch(createOrUpdatePortfolioGroup({
        name,
        portfolio_ids
      })).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to create portfolio group:', error);
      throw error;
    }
  }, [dispatch]);

  // Add portfolios to an existing group
  const addPortfoliosToExistingGroup = useCallback(async (group_id, newPortfolioIds) => {
    try {
      const existingGroup = portfolioGroups.find(group => group.group_id === group_id);
      if (!existingGroup) {
        throw new Error('Group not found');
      }

      // Combine existing and new portfolio IDs, removing duplicates
      const updatedPortfolioIds = Array.from(new Set([
        ...existingGroup.portfolio_ids,
        ...newPortfolioIds
      ]));

      const result = await dispatch(createOrUpdatePortfolioGroup({
        group_id,
        name: existingGroup.name,
        portfolio_ids: updatedPortfolioIds
      })).unwrap();

      return result;
    } catch (error) {
      console.error('Failed to add portfolios to group:', error);
      throw error;
    }
  }, [dispatch, portfolioGroups]);

  // Remove portfolios from a group
  const removePortfoliosFromGroup = useCallback(async (group_id, portfolioIdsToRemove) => {
    try {
      const existingGroup = portfolioGroups.find(group => group.group_id === group_id);
      if (!existingGroup) {
        throw new Error('Group not found');
      }

      const updatedPortfolioIds = existingGroup.portfolio_ids.filter(
        id => !portfolioIdsToRemove.includes(id)
      );

      const result = await dispatch(createOrUpdatePortfolioGroup({
        group_id,
        name: existingGroup.name,
        portfolio_ids: updatedPortfolioIds
      })).unwrap();

      return result;
    } catch (error) {
      console.error('Failed to remove portfolios from group:', error);
      throw error;
    }
  }, [dispatch, portfolioGroups]);

  // Rename a group
  const renameGroup = useCallback(async (group_id, newName) => {
    try {
      const existingGroup = portfolioGroups.find(group => group.group_id === group_id);
      if (!existingGroup) {
        throw new Error('Group not found');
      }

      const result = await dispatch(createOrUpdatePortfolioGroup({
        group_id,
        name: newName,
        portfolio_ids: existingGroup.portfolio_ids
      })).unwrap();

      return result;
    } catch (error) {
      console.error('Failed to rename group:', error);
      throw error;
    }
  }, [dispatch, portfolioGroups]);

  // Get a specific group by ID
  const getGroupById = useCallback((group_id) => {
    return portfolioGroups.find(group => group.group_id === group_id);
  }, [portfolioGroups]);

  return {
    portfolioGroups, // The full list of portfolio groups
    createNewGroup,
    addPortfoliosToExistingGroup,
    removePortfoliosFromGroup,
    renameGroup,
    getGroupById
  };
};